import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { isEditableTarget } from '../manualPlay';
import TwoJointPoleSceneMultiplayer from '../visuals/TwoJointPoleSceneMultiplayer';
import { TjpSwitchableScene } from '../visuals/TjpSceneView';
import TwoJointPoleModePicker from './TwoJointPoleModePicker';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import {
  INTERP_DELAY_MS, TjpRoomSocket, beaconLeave, clearSeat, createSnapshotBuffer, heldAction,
  inviteLink, loadSeat, pushSnapshot, roomIdFromSearch, sampleSnapshot, saveSeat, tjpRooms,
} from './tjpOnlineNet';
import { clearRoomParam, inviteRoomStatus, roomErrorMessage } from './onlineRoomsNet';

const GAME_ID = 'two_joint_pole_multiplayer';
const ACCENT = GAME_ACCENT.two_joint_pole;
const UPLOAD_URL = ARCADE.two_joint_pole?.UPLOAD_URL || 'http://localhost:3000';
const LOBBY_TIMEOUT_S = 45;
const SEAT_LABEL = { player_1: 'Blue (P1)', player_2: 'Orange (P2)' };

export function roomIdFromLocation() {
  if (typeof window === 'undefined') return null;
  return roomIdFromSearch(window.location.search);
}

const PANEL_SX = {
  p: 2,
  borderRadius: 2,
  border: '1px solid rgba(142,140,216,0.35)',
  bgcolor: 'rgba(12, 16, 32, 0.92)',
  width: '100%',
  maxWidth: 560,
};

/**
 * Online 2P: host creates a room (or quick-matches), the second browser joins
 * by link/code. The gateway ticks the match; this page only sends its own
 * seat's input and renders interpolated server snapshots.
 */
export default function TwoJointPoleOnline({ gameMode, setGameMode }) {
  const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
  const [grant, setGrant] = useState(null);
  /** idle | waiting | ready | running | paused | finished */
  const [phase, setPhase] = useState('idle');
  const [conn, setConn] = useState('closed');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [seats, setSeats] = useState({});
  const [waitingRooms, setWaitingRooms] = useState([]);
  const [code, setCode] = useState(() => roomIdFromLocation() || '');
  const [busy, setBusy] = useState(false);
  const [physics, setPhysics] = useState({ player_1: { cart_position: -1 }, player_2: { cart_position: 1 } });
  const [shake, setShake] = useState({ player_1: 0, player_2: 0 });
  const [scores, setScores] = useState({ player_1: 0, player_2: 0 });
  const [lobbyLeft, setLobbyLeft] = useState(null);

  const sockRef = useRef(null);

  const endedRef = useRef(false);
  const bufRef = useRef(createSnapshotBuffer());
  const keysRef = useRef(new Set());
  const lastSentRef = useRef('stay');
  const grantRef = useRef(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const live = phase === 'waiting' || phase === 'ready' || phase === 'running' || phase === 'paused';

  const onMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'welcome':
      case 'peer':
      case 'start':
      case 'lobby':
        if (msg.phase) setPhase(msg.phase);
        if (msg.seats) setSeats(msg.seats);
        if (typeof msg.lobby_seconds_remaining === 'number') setLobbyLeft(msg.lobby_seconds_remaining);
        if (msg.type === 'start') setMessage('Fight! Last upright pole wins.');
        if (msg.type === 'lobby' && msg.message) setMessage(msg.message);
        if (msg.type === 'welcome' && msg.snapshot) {
          pushSnapshot(bufRef.current, { ...msg.snapshot, tick: msg.tick }, performance.now());
        }
        break;
      case 'state':
        pushSnapshot(bufRef.current, msg, performance.now());
        if (msg.phase && msg.phase !== phaseRef.current) setPhase(msg.phase);
        if (msg.players) {
          setShake({ player_1: msg.players.player_1?.shake || 0, player_2: msg.players.player_2?.shake || 0 });
          setScores({ player_1: msg.players.player_1?.score || 0, player_2: msg.players.player_2?.score || 0 });
        }
        break;
      case 'end':
          endedRef.current = true;
        pushSnapshot(bufRef.current, msg, performance.now());
        setPhase('finished');
        setResult({ winner: msg.winner, reason: msg.reason });
        clearSeat();
        break;
      case 'kicked':
        setMessage('This seat was opened in another tab.');
        break;
      case 'error':
        if (endedRef.current && msg.code === 'room_finished') break; // result already shown (incl. replayed end)
        if (msg.code === 'room_finished' || msg.code === 'room_not_found' || msg.code === 'bad_seat_token') {
          setMessage('That room is no longer available.');
          clearSeat();
          setPhase('idle');
          setGrant(null);
        }
        break;
      default:
        break;
    }
  }, []);

  const openSocket = useCallback((g) => {
    if (sockRef.current) sockRef.current.close();
    bufRef.current = createSnapshotBuffer();
    lastSentRef.current = 'stay';
    grantRef.current = g;
    setGrant(g);
    setResult(null);
    setPhase(g.phase || 'waiting');
    setSeats(g.seats || {});
    saveSeat(g);
    const s = new TjpRoomSocket(g, {
      onMessage,
      onStatus: (st) => setConn(st),
      graceMs: (g.reconnect_grace_s || 20) * 1000,
    });
    sockRef.current = s;
    s.connect();
  }, [onMessage]);

  const run = useCallback(async (fn) => {
    setBusy(true);
    setMessage('');
    try {
      const g = await fn();
      endedRef.current = false;
      openSocket(g);
    } catch (e) {
      setMessage(roomErrorMessage(e));
      clearRoomParam('tjp_room');
    } finally {
      setBusy(false);
    }
  }, [openSocket]);

  const createRoom = () => run(() => tjpRooms.create({ lobby_timeout_s: LOBBY_TIMEOUT_S }));
  const quickMatch = () => run(() => tjpRooms.match({ lobby_timeout_s: LOBBY_TIMEOUT_S }));
  const joinRoom = (id) => run(() => tjpRooms.join(String(id || code).trim()));

  const leave = useCallback(() => {
    const g = grantRef.current;
    if (g) beaconLeave(g.room_id, g.seat_token);
    if (sockRef.current) sockRef.current.close();
    sockRef.current = null;
    grantRef.current = null;
    clearSeat();
    setGrant(null);
    setPhase('idle');
    setConn('closed');
  }, []);

  // Resume a seat after a reload (same tab, inside the grace window).
  useEffect(() => {
    const saved = loadSeat();
    if (saved && saved.room_id && saved.seat_token) {
      tjpRooms.info(saved.room_id)
        .then((info) => {
          if (info && info.phase !== 'finished') openSocket({ ...info, ...saved });
          else clearSeat();
        })
        .catch(() => clearSeat());
    }
    return () => { if (sockRef.current) sockRef.current.close(); };
  }, [openSocket]);

  // Invite link (?tjp_room=<id>): auto-join once on load. Skipped when a saved
  // seat is being resumed; the ref guards StrictMode double effects.
  const autoJoinRef = useRef(false);
  useEffect(() => {
    if (autoJoinRef.current) return;
    autoJoinRef.current = true;
    const id = roomIdFromLocation();
    if (!id) return;
    const saved = loadSeat();
    if (saved && saved.room_id && saved.seat_token) return;
    inviteRoomStatus(tjpRooms, id).then((st) => {
      if (st.join) { run(() => tjpRooms.join(id)); return; }
      clearRoomParam('tjp_room'); // a reload must not try this room again
      setMessage(st.message);
    });
  }, [run]);

  // Leave on tab close (the server would forfeit after the grace window anyway).
  useEffect(() => {
    const onHide = () => {
      const g = grantRef.current;
      if (g && phaseRef.current === 'waiting') beaconLeave(g.room_id, g.seat_token);
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Waiting-room list while idle.
  useEffect(() => {
    if (phase !== 'idle') return undefined;
    let stop = false;
    const load = () => tjpRooms.list().then((r) => { if (!stop) setWaitingRooms(r); }).catch(() => {});
    load();
    const id = setInterval(load, 4000);
    return () => { stop = true; clearInterval(id); };
  }, [phase]);

  // Lobby countdown (display only; the server decides).
  useEffect(() => {
    if (phase !== 'waiting' || lobbyLeft == null) return undefined;
    const id = setInterval(() => setLobbyLeft((v) => (v == null ? v : Math.max(0, v - 1))), 1000);
    return () => clearInterval(id);
  }, [phase, lobbyLeft]);

  // Render loop: interpolate between server snapshots INTERP_DELAY_MS behind.
  useEffect(() => {
    if (!grant) return undefined;
    let raf = 0;
    const loop = () => {
      const p = sampleSnapshot(bufRef.current, performance.now() - INTERP_DELAY_MS);
      if (p) setPhysics(p);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [grant]);

  // Keyboard → this seat's action only (← → / A D hold, Space punch).
  useEffect(() => {
    if (!grant) return undefined;
    const sendHeld = () => {
      const a = heldAction(keysRef.current);
      if (a !== lastSentRef.current && sockRef.current) {
        if (sockRef.current.sendAction(a)) lastSentRef.current = a;
      }
    };
    const down = (e) => {
      if (isEditableTarget(e.target)) return;
      if (e.key === ' ' || e.key.startsWith('Arrow')) e.preventDefault();
      if (e.repeat) return;
      if (e.key === ' ') {
        if (sockRef.current) sockRef.current.sendAction('punch');
        return;
      }
      keysRef.current.add(e.key);
      sendHeld();
    };
    const up = (e) => {
      keysRef.current.delete(e.key);
      sendHeld();
    };
    const blur = () => { keysRef.current.clear(); sendHeld(); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    // Re-send the held key after a reconnect (server resets the seat to stay).
    const resend = setInterval(() => {
      if (conn === 'open' && lastSentRef.current !== 'stay' && sockRef.current) {
        sockRef.current.sendAction(lastSentRef.current);
      }
    }, 1000);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
      clearInterval(resend);
    };
  }, [grant, conn]);

  const mySeat = grant?.seat;
  const other = mySeat === 'player_1' ? 'player_2' : 'player_1';
  const otherSeat = seats[other];
  const link = grant ? inviteLink(grant.room_id) : '';

  let banner = '';
  if (phase === 'waiting') banner = `Waiting for an opponent${lobbyLeft != null ? ` · ${Math.ceil(lobbyLeft)}s, then vs computer` : ''}`;
  else if (phase === 'ready') banner = 'Opponent found — connecting…';
  else if (phase === 'paused') banner = 'Opponent disconnected — waiting for them to come back…';
  else if (conn === 'reconnecting') banner = 'Connection lost — reconnecting…';

  let resultText = '';
  if (result) {
    if (!result.winner) resultText = result.reason === 'cancelled' ? 'Room closed.' : 'Draw!';
    else resultText = result.winner === mySeat ? 'You win!' : 'You lose';
    if (result.reason === 'forfeit') resultText += result.winner === mySeat ? ' (opponent left)' : ' (forfeit)';
  }

  return (
    <ArcadeGameShell
      title="Two-Joint"
      titleAccent="Pole"
      subtitle="Online 2P — each player on their own computer, the server runs the match"
      accentColor={ACCENT}
      headerContent={(
        <TwoJointPoleModePicker value={gameMode} onChange={setGameMode} accentColor={ACCENT} disabled={live} />
      )}
      boilerplate={GAME_BOILERPLATE.two_joint_pole}
      session={session}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%' }}>
        {!grant && (
          <Box sx={PANEL_SX} data-testid="tjp-online-lobby">
            <Typography sx={{ color: COLOR.text, fontWeight: 700, mb: 1 }}>Play online</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
              <Button variant="contained" onClick={createRoom} disabled={busy} sx={{ bgcolor: ACCENT }}>
                Create room
              </Button>
              <Button variant="outlined" onClick={quickMatch} disabled={busy}>Quick match</Button>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                label="Room code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && code.trim()) joinRoom(); }}
                inputProps={{ 'aria-label': 'Room code' }}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={() => joinRoom()} disabled={busy || !code.trim()}>Join</Button>
            </Stack>
            <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', mb: 0.5 }}>
              Open rooms
            </Typography>
            {waitingRooms.length === 0 && (
              <Typography sx={{ color: COLOR.textSubtle, fontSize: '0.8rem' }}>
                None right now — create one and send the link to a friend.
              </Typography>
            )}
            <Stack spacing={0.5}>
              {waitingRooms.map((r) => (
                <Stack key={r.room_id} direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ color: COLOR.text, fontSize: '0.85rem', flex: 1 }}>
                    {r.seats?.player_1?.username || 'Guest'} · {r.room_id}
                  </Typography>
                  <Button size="small" onClick={() => joinRoom(r.room_id)} disabled={busy}>Join</Button>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {grant && (
          <Box sx={{ ...PANEL_SX, py: 1.25 }} data-testid="tjp-online-status">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Chip size="small" label={`You: ${SEAT_LABEL[mySeat] || mySeat}`} sx={{ color: COLOR.text, borderColor: ACCENT }} variant="outlined" />
              <Chip
                size="small"
                variant="outlined"
                sx={{ color: COLOR.textMuted }}
                label={otherSeat ? (otherSeat.human ? `${otherSeat.username || 'Guest'}${otherSeat.connected ? '' : ' (offline)'}` : 'Computer') : 'Open seat'}
              />
              <Chip size="small" variant="outlined" sx={{ color: COLOR.textMuted }} label={`${scores.player_1.toFixed(1)} – ${scores.player_2.toFixed(1)}`} />
              <Box sx={{ flex: 1 }} />
              <Button size="small" color="inherit" onClick={leave} sx={{ color: COLOR.textMuted }}>
                {phase === 'finished' ? 'Back' : 'Leave'}
              </Button>
            </Stack>
            {phase === 'waiting' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: 'center' }}>
                <Typography sx={{ color: COLOR.text, fontSize: '0.85rem' }}>
                  Room code <b>{grant.room_id}</b>
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { try { navigator.clipboard.writeText(link); setMessage('Invite link copied.'); } catch (_) { setMessage(link); } }}
                >
                  Copy invite link
                </Button>
              </Stack>
            )}
          </Box>
        )}

        {(banner || message || resultText) && (
          <Typography role="status" sx={{ color: resultText ? COLOR.text : COLOR.textMuted, fontWeight: resultText ? 700 : 400 }}>
            {resultText || banner || message}
          </Typography>
        )}

        <Box sx={{ borderRadius: 3, border: '1px solid rgba(142,140,216,0.3)', overflow: 'hidden', bgcolor: '#0c1a24' }}>
          <TjpSwitchableScene
            Scene2D={TwoJointPoleSceneMultiplayer}
            player1_physics={physics.player_1}
            player2_physics={physics.player_2}
            player1_shake={shake.player_1}
            player2_shake={shake.player_2}
            width={560}
            height={320}
          />
        </Box>
        <Typography sx={{ color: COLOR.textSubtle, fontSize: '0.75rem' }}>
          ← → or A D move · Space punch · you control {SEAT_LABEL[mySeat] || 'your cart'} only
        </Typography>
      </Box>
    </ArcadeGameShell>
  );
}
